/**
 * ============================================================================
 * useMetricDrillDownQuery — Phase 6b frontend (Quality Metrics Dashboard v2)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook that fetches the list of entities contributing
 *          to a single metric — used by <MetricDrillDownDrawer>. The query is
 *          enabled only while the drawer is open so we don't pay for entries
 *          the user never expands.
 * Endpoint: GET /api/v1/quality-metrics/{metricKey}/drill-down
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type { MetricKey } from './useDashboardQuery';

export interface DrillDownEntity {
  /** Stable per-entity id (deviation id, capa id, doc id, …) */
  entity_id: string;
  /** "DEVIATION" | "CAPA" | "DOCUMENT" | "BATCH_RECORD" | … */
  entity_kind: string;
  /** Short headline — what the user clicks. */
  title: string;
  /** Optional subtitle (e.g., "QA-OPEN · aged 12 d"). */
  subtitle: string | null;
  /** Per-entity contribution to the parent metric (raw count or percent). */
  contribution: number;
  /** Optional payload for the row (owner, site_code, etc.). */
  payload: Record<string, unknown>;
}

export interface DrillDownResponse {
  metric_key: MetricKey;
  total: number;
  items: DrillDownEntity[];
  generated_at_utc: string;
}

export function useMetricDrillDownQuery(
  metricKey: MetricKey | null,
): UseQueryResult<DrillDownResponse> {
  return useQuery<DrillDownResponse>({
    queryKey: ['quality-metrics', 'drill-down', metricKey],
    queryFn: ({ signal }) =>
      apiFetch<DrillDownResponse>(
        `/api/v1/quality-metrics/${encodeURIComponent(metricKey ?? '')}/drill-down`,
        { signal },
      ),
    // Only run when the drawer is open and we have a metric key in hand.
    enabled: metricKey != null,
    // The drill-down doesn't need the 60 s poll — the user is reading rows,
    // and metrics-svc emits a `metric.computed` event we could react to in
    // the future. For now a single fetch per open is enough.
    staleTime: 30_000,
  });
}
